import { COUNTRIES } from '../config/countries-data'
import UserManager from './UserManager'
import PaymentManager from './PaymentManager'
import { PaymentMethodType } from './PaymentManager.types'
import StorageManager from './StorageManager'
import { giftNoteManager } from './GiftNoteManager'
import { shippingOptionsManager } from './ShippingOptionsManager'
import {
  isOptionExpress,
  isOptionPriority,
} from '../stores/shippingStore/shipping.utils'
import { SHIPPING_TOP_QUEUE_KEY } from '../stores/shippingStore/shipping.consts'
import { v4 as uuid } from 'uuid'
import { computeUpscaleEligibility } from '../utils/upscaleEligibility'

import { analytics, EVENT_NAMES } from './Analytics/Analytics'
import { getOrderPhotoBooksProps } from './Analytics/photoBooksAnalytics'
import { getSerializedBookPhotos } from './PhotoBook/photoBookDTO'
import { getAddressStore } from '../stores/RootStoreProvider'

import { promoCodeManager } from './PromoCodeManager'

import { translateManager as t } from './TranslateManager'

import { TILE_FRAMES, TILE_TYPES } from '../config/tile-filters'
import { countryManager } from './CountryManager'

import {
  convertPriceToUSDCurrency,
  roundDecimal,
  capitalizeFirstLetter,
} from '../utils/utils'
import {
  booksBannerManager,
  genericBannerManager,
} from './GenericBannerManager'

import { FRAME_COLORS } from '../core/Tile/consts'
import {
  CANVAS_WRAPS_COLOR,
  FILTERS,
  getBleed,
  getTileFilter,
  GiftCardType,
  isCenterpiece,
  isCollage,
  DEFAULT_MAT_COLOR,
  MATERIAL_TYPES,
  MATTING,
  ORDER_ITEM_TYPE,
  TILE_CONTENT,
  TILE_CONTEXT,
  TILE_SIZES,
  isPhoto,
  isArtPhoto,
  isAIGenPhoto,
} from '@mixtiles/web-backend-shared'
import { getMatColorValue } from '../utils/matColorUtils'
import { isTileEqualToLineItem } from './tilePricingMatch'
import { logger } from './logger'
import { getOrderSessionId } from './OrderSession'
import {
  getTileStyleData,
  isMattingEnabledForMaterial,
} from './tilesStyling.js'
import pick from 'lodash/pick'
import mapValues from 'lodash/mapValues'
import sumBy from 'lodash/sumBy'
import {
  ATTRIBUTION_SURVEY_KEY,
  WHO_IS_IT_FOR_KEY,
  WHO_IS_IT_FOR_OPTIONS,
} from '../pages/EmailCapturePage/EmailCapturePage.consts'
import {
  generateCartAnalytics,
  generateCartItemAnalytics,
} from './Analytics/cartAnalytics'
import {
  getCropsDistribution,
  getMinimumOrderFeeProps,
  getPromiseDateProps,
  getShippingPricingProps,
  getSmartCropAdjustmentMetrics,
  trackOrderCompleted,
  getViewContentParams,
} from './Analytics/orderAnalytics'
import {
  getGiftCardProps,
  getGiftCardRedeemedProps,
} from './Analytics/giftCardAnalytics'
import { getTileMaterialType, getTilesMaterials } from './tileMaterial'
import { FRAMELESS_CENTERPIECE_CROP_RECT } from '../components/CenterpieceSelection/Centerpieces.const'
import {
  getCollageProperties,
  getMaterialProperties,
  getTileDistribution,
} from './Analytics/photoAnalytics/photoAnalytics'
import { getPricingAnalyticsData } from '../components/Pricing/PricingUtil'
import {
  getTileCropParams,
  getTileSize,
  getTileSizesCount,
  getTilesSizesNames,
} from '../components/PhotoBank/tileUtils'
import {
  getOrderFaceAndDPIMetrics,
  getSmallestFaceMetrics,
  getTileDPI,
  getTileFaceMetrics,
} from './Analytics/dpiMetrics'
import { shouldWarnTileLowQuality } from '../pages/PhotoStyler/TileWarning/tileWarning.utils'
import { LANGUAGES } from '../locale/Translation.consts'
import { generateProductCountEventProperties } from './Analytics/productsAnalytics'
import { getOrderLocalId, resetOrderLocalId } from './OrderUtils'
import { isFramedMaterial } from '../utils/materialTypeUtils'
import { getPhotoCroppingParams } from './Collage/collageService'
import {
  serverSideAnalytics,
  ServerSideAnalyticsPlatform,
} from './Analytics/ServerSideAnalytics'
import { PRODUCT_TYPES, productTypeState } from './ProductTypeState'
import { isGiftCardOrder } from '../pages/GiftCard/utils'
import { getSha256 } from '../utils/cryptoUtils'
import { getVATPercentage } from './PricingUtils'
import { getTileArtworkProperties } from './Analytics/artCollectionAnalytics'
import { scaleCropParams } from '../utils/cropUtils'
import { isCanvasGenerativeWrap } from '../utils/canvasWrapUtils'
import { experimentManager } from './ExperimentManager/ExperimentManager'
import { calculateArtworkDimensions } from './artCollectionService'
import { userState } from './User/userState'
import { normalizeAddress } from '../stores/addressStore/address.utils'
import { getPayPalKey } from '../stores/paymentStore/payment.utils'
import omit from 'lodash/omit'
import { getAIGenPhotoTileAnalyticsProps } from '../pages/AICreation/utils'
import { getCartUpsellTilesAnalyticsProps } from '../features/cartUpsells/cartUpsellTiles'
import { mixtilesAxios, extractErrorMessage } from '../utils/ApiUtils.js'

export const LAST_ORDER_DELIVERY_PROMISE = 'lastOrderDeliveryPromise'

// Non-analytics side-effects shared by the single and group checkout flows.
// The active-orders badge isn't touched here — reloadAccount() after the order
// reconciles it from the server count.
export async function applyPreOrderSideEffects({ email }) {
  // A user with an email but no unifiedUid is an invalid state; force the backfill.
  const currUser = userState.getUser()
  if (!currUser.unifiedUid) {
    await UserManager.setUserEmail({
      email,
      refetchExperiments: false,
      emailConsent: email
        ? UserManager.hasExplicitEmailConsent(email)
        : undefined,
    })
  }
}

export function wasCouponRedeemedInOrders(orders) {
  const activeCouponCode = promoCodeManager.getDiscountCouponCode()
  if (!activeCouponCode) {
    return false
  }
  return orders.some(
    (order) => order?.coupon?.toUpperCase() === activeCouponCode.toUpperCase()
  )
}

export function applyPostOrderSideEffects({
  deliveryPromise,
  hasUsedCredits,
  wasDiscountCouponRedeemed,
  isGiftCard,
}) {
  resetOrderLocalId()
  genericBannerManager.resetBannerPromoCodeUsageInSession()
  booksBannerManager.resetBannerPromoCodeUsageInSession()

  if (isGiftCard) {
    return
  }

  // Remove coupon and update tiles credits (only if there was one)
  if (hasUsedCredits) {
    promoCodeManager.afterOrderCompleted(wasDiscountCouponRedeemed)
  }
  // Remove the gift note, if exists
  giftNoteManager.setGiftNote(null)
  // Remove shipping options and shipping option type
  shippingOptionsManager.clearShippingOptions()
  StorageManager.set(LAST_ORDER_DELIVERY_PROMISE, deliveryPromise)
}

class OrderManager {
  static _getCommonOrderParams({
    orderPriceSummary,
    address,
    deliveryPromise,
    paymentMethod,
    paymentMethodId,
    paymentIntentId,
    shouldValidateAddress,
  }) {
    const { totalPrice, currency } = orderPriceSummary
    const facebookEventName = 'On-CreateOrder-event-BACKEND'
    const fbEventCustomData = { currency, value: totalPrice }
    const facebookEvent = serverSideAnalytics.getServerSideEvent(
      facebookEventName,
      fbEventCustomData,
      null,
      [ServerSideAnalyticsPlatform.Facebook]
    )
    // Common properties that are required in order to complete an order
    const params = {
      platform: 'web',
      appVersion: window.KEYS.buildCommit || 'default-build-commit',
      clientTotalCost: totalPrice,
      clientCurrency: currency,
      localId: getOrderLocalId(),
      language: t.getLanguage() || LANGUAGES.ENGLISH,
      locale: countryManager.getPricingCountry(),
      coupon: getDiscountCoupon(orderPriceSummary),
      analyticsEvent: facebookEvent,
      orderSessionId: getOrderSessionId(),
      paymentData: this.preparePaymentData({
        totalPrice,
        paymentMethod,
        paymentMethodId,
        paymentIntentId,
      }),
      shippingOption: deliveryPromise,
      user: this._prepareUser(),
      address: this._prepareAddress({
        address,
        shouldValidateAddress,
      }),
      orderType: 'online',
      giftNote: giftNoteManager.getGiftNote(),
      productType: productTypeState.getProductType(),
    }

    params.giftCodes = promoCodeManager.getGiftCardCreditsCodes()

    return params
  }

  static _getGiftCardOrderParams({
    orderPriceSummary,
    orderItems,
    commonOrderParams,
    productType,
  }) {
    const {
      recipientEmail,
      recipients,
      giftMessage,
      selectedDesign,
      giftCardType,
      senderName,
      selectedOffer,
      companyName,
    } = orderItems[0].metadata

    // since those params can be received from sessionStorage or from the regular flow, we always convert to Date object
    const dateToSend = new Date(orderItems[0].metadata.dateToSend)

    const giftCardProps = {
      customerName: companyName || senderName || UserManager.getUserFullName(),
      designImageUrl: selectedDesign.imageUrl,
      type: giftCardType,
    }
    if (productType === PRODUCT_TYPES.BUSINESS) {
      giftCardProps.recipients = recipients
    } else if (productType === PRODUCT_TYPES.GIFT_CARD) {
      giftCardProps.recipientEmail = recipientEmail
    }

    giftCardProps.dateToSend = dateToSend.toISOString()
    giftCardProps.userTimeZone =
      Intl.DateTimeFormat().resolvedOptions().timeZone

    if (giftMessage) {
      giftCardProps.message = giftMessage
    }
    if (selectedOffer.type === GiftCardType.VALUE) {
      giftCardProps.initialValue = selectedOffer.value
    } else {
      giftCardProps.orderSize = selectedOffer.value
    }

    if (selectedOffer.type === GiftCardType.BOOKS) {
      giftCardProps.pricingProduct =
        orderPriceSummary.originalProducts[0].pricingProduct
    }

    // Some of the common order params are not relevant for gift cards, pick only the relevant ones
    const giftCardCommonParams = pick(commonOrderParams, [
      'platform',
      'appVersion',
      'clientTotalCost',
      'clientCurrency',
      'localId',
      'language',
      'locale',
      'coupon',
      'analyticsEvent',
      'orderSessionId',
      'paymentData',
    ])
    return {
      ...giftCardCommonParams,
      customerEmail: UserManager.getUserEmail(),
      ...giftCardProps,
    }
  }

  // Get all order params which are sent in the create order API call
  static getOrderParams({
    deliveryPromise,
    address,
    paymentMethod,
    paymentMethodId,
    paymentIntentId,
    tiles,
    orderItems,
    orderPriceSummary,
    shouldValidateAddress,
    topazLabsEnabled,
  }) {
    const { priceBeforeTax, freeTiles, pricingId, productType } =
      orderPriceSummary

    const commonOrderParams = this._getCommonOrderParams({
      orderPriceSummary,
      address,
      deliveryPromise,
      paymentMethod,
      paymentMethodId,
      paymentIntentId,
      shouldValidateAddress,
    })

    if (isGiftCardOrder(productType)) {
      return this._getGiftCardOrderParams({
        orderPriceSummary,
        orderItems,
        commonOrderParams,
        productType,
      })
    } else if (productType === PRODUCT_TYPES.PHOTO_BOOK) {
      const orderProducts = orderItems.map(
        ({ itemId, itemType, metadata, quantity }) => ({
          id: itemId,
          productType: itemType,
          // The single-product checkout carries no quantity on its one item:
          // it prices one row per copy instead.
          quantity: quantity ?? orderPriceSummary.products.length,
          metadata,
        })
      )
      return {
        ...commonOrderParams,
        orderProducts,
        orderSize: sumBy(
          orderProducts,
          (orderProduct) => orderProduct.quantity
        ),
        orderDraft: {
          photoBook: orderItems[0].metadata,
        },
      }
    } else {
      const orderData = {
        ...commonOrderParams,
        priceBeforeTax,
        pricingId: pricingId || 'WEB',
        appSupportsRetailPricing: false,
        usingFallbackPricing: false,
        newTileActivated: true,
        appSupportsNewShippingNotifications: false,
        redemptions: promoCodeManager.getTilesCreditsToRedeem(freeTiles),
        unifyOrientations: false,
        topazLabsEnabled,
      }

      orderData.uploads = this._prepareUploads({ tiles })

      // Content Tiles data
      const contentTilesCount = tiles.filter(
        (tile) => tile.contentSet !== null
      ).length
      if (contentTilesCount > 0) {
        const tilesContentSets = tiles.map((tile) => tile.contentSet)
        orderData.contentSets = [
          ...new Set(
            tilesContentSets.filter((contentSet) => contentSet != null)
          ),
        ]
        orderData.contentTilesCount = contentTilesCount
      }

      // Art Suggestions data
      const artTileUrls = tiles
        .filter((tile) => tile.artTileUrl)
        .map((tile) => tile.artTileUrl)
      const artTilesCount = artTileUrls.length

      if (artTilesCount > 0) {
        orderData.artTileUrls = [...new Set(artTileUrls)]
        orderData.artTilesCount = artTilesCount
      }

      if (StorageManager.get(SHIPPING_TOP_QUEUE_KEY)) {
        orderData.topQueue = true
      }

      orderData.tileType = this._prepareTileType({
        uploads: orderData.uploads,
      })

      if (tiles.length > 0 && tiles.some(isArtPhoto)) {
        orderData.artistsData = tiles.filter(isArtPhoto).map((tile, index) => ({
          artworkId: tile.artwork.uid,
          artistId: tile.artwork.artist?.uid,
          artistName: tile.artwork.artist?.name,
          title: tile.artwork.title,
          category:
            tile.artwork.classifications?.find((c) => c.type === 'Category')
              ?.name || '',
          size: tile.tileSize,
          frameColor: tile.customStyle.frameColor,
          currency: 'USD',
          artworksCount: 1,
          price: convertPriceToUSDCurrency(
            orderPriceSummary.products[index].priceAfterDiscount,
            orderPriceSummary.currency
          ),
        }))
      }

      if (orderPriceSummary.creditUsed !== 0) {
        orderData.credit = orderPriceSummary.creditUsed
      }

      orderData.mixtiles20 = true
      orderData.orderDraft = {
        tiles,
      }
      return orderData
    }
  }

  static _getOrderEndpoint(orderPriceSummary) {
    if (orderPriceSummary.validated) {
      switch (orderPriceSummary.productType) {
        case PRODUCT_TYPES.GIFT_CARD:
          return 'v5/order/giftCard'
        case PRODUCT_TYPES.BUSINESS:
          return 'v5/order/business'
        case PRODUCT_TYPES.PHOTO_BOOK:
          return 'v5/order/photo-books'
        default:
          return 'v5/order'
      }
    } else {
      return 'v4/order'
    }
  }

  /** @param {import('./OrderManager.types').CreateOrderParams & { paymentIntentId: string | undefined }} params */
  static async createOrder({
    deliveryPromise,
    address,
    paymentMethod,
    paymentMethodId,
    paymentIntentId,
    tiles,
    orderItems,
    orderPriceSummary,
    shouldValidateAddress,
    requestConfig,
    topazLabsEnabled,
    bagAnalytics,
  }) {
    const { totalPrice, tax, currency } = orderPriceSummary

    const initialUserParams = await UserManager.getInitialUserParams()
    const orderParams = this.getOrderParams({
      deliveryPromise,
      address,
      paymentMethod,
      paymentMethodId,
      paymentIntentId,
      tiles,
      orderItems,
      orderPriceSummary,
      shouldValidateAddress,
      topazLabsEnabled,
    })

    const config = requestConfig || { headers: {} }
    orderParams.checkoutId = orderPriceSummary.checkoutId

    if (orderPriceSummary?.giftsBalances?.length) {
      // check if any of them has source referral
      const hasReferral = orderPriceSummary.giftsBalances.some(
        (balance) => balance.source === 'referral'
      )

      if (hasReferral) {
        analytics.track(EVENT_NAMES.REFERRAL_REDEEMED)
      }
    }

    // Diagnostic: detect "stale pricingSummary cart" — when the cart that produced
    // orderPriceSummary.totalPrice (used as clientTotalCost) differs from the actual tiles being
    // POSTed. We've seen rare price_mismatch cases (e.g. CAPTURE55 BUYOVER, mixed-size cart)
    // where this divergence underlies the failure. Query Loggly for `order/pricing cart
    // divergence` with cartMismatch=true to surface candidates. INF-534.
    //
    // We compare two counts that should be apples-to-apples:
    //   - orderPriceSummary.totalItems: count of PRODUCT-type summaryItems the server returned
    //     (one per tile, INCLUDES centerpieces) — i.e., `products.length` in formatPricingSummary
    //   - tiles.length: count of tile uploads being POSTed (also INCLUDES centerpieces)
    // Note: we deliberately don't compare `totalTiles` (which excludes centerpieces) or
    // `orderItems.length` (which is a cart-row count, not a tile count) — those would produce
    // false positives. This check catches count divergence but won't catch same-count carts
    // with different sizes/materials (which would still produce a price mismatch). A future
    // refinement could compare a per-(size, material, frameColor) breakdown.
    // Skip tile-count comparison for non-tile orders — photo books have 0 tile uploads by
    // design and would always fire cartMismatch:true, generating noise (91+ false positives/hour
    // observed in production). The check is only meaningful for tile orders
    const isTileOrder = orderParams?.productType === PRODUCT_TYPES.CLASSIC
    const pricedTileCount = orderPriceSummary?.totalItems ?? null
    const actualTileCount = tiles?.length ?? 0
    const cartMismatch =
      isTileOrder &&
      pricedTileCount !== null &&
      pricedTileCount !== actualTileCount
    logger.info('order/pricing cart divergence', {
      cartMismatch,
      pricedTileCount,
      actualTileCount,
      productType: orderParams?.productType,
      pricedTotalPrice: orderPriceSummary?.totalPrice,
      pricedCheckoutId: orderPriceSummary?.checkoutId,
    })

    // Diagnostic: detect "gift codes lost between pricing and order POST" — Kelly's pattern
    // (Klarna/Stripe redirect, gift card discount present in pricing summary but giftCodes
    // missing from request body). Local repro of normal redirect flow shows gift codes ARE
    // preserved via localStorage, so this only fires for rare environments where localStorage
    // was wiped mid-flow (Safari ITP, private mode, cross-tab, etc.). After 1-2 weeks live,
    // query Loggly for `order/pricing gift code state` with giftCodeMismatch=true to confirm
    // scope before shipping a restoration fix. See INF-520 / Kelly's case.
    //
    // Also captures #3 (gift card + currency mismatch residual post-PR #5480):
    // pricedCurrency vs orderCurrency can still diverge for gift-card orders if the gift card
    // was issued in a different currency than the session detected. giftCodeMismatch=false but
    // pricedCurrency !== orderCurrency in those cases.
    const requestGiftCodes = orderParams?.giftCodes ?? []
    const pricedGiftCardLineCount =
      orderPriceSummary?.giftCardCreditItems?.length ?? 0
    const pricedHasGiftCardDiscount = pricedGiftCardLineCount > 0
    const giftCodeMismatch =
      pricedHasGiftCardDiscount && requestGiftCodes.length === 0
    const pricedCurrency = orderPriceSummary?.currency ?? null
    const orderCurrency = orderParams?.clientCurrency ?? null
    logger.info('order/pricing gift code state', {
      giftCodeMismatch,
      hasGiftCodesInRequest: requestGiftCodes.length > 0,
      requestGiftCodeCount: requestGiftCodes.length,
      pricedHasGiftCardDiscount,
      pricedGiftCardLineCount,
      // #3 / #5 currency diagnostic: surfaces gift-card currency mismatch AND locale-routing
      // failures (GBP/CAD user hitting wrong-currency backend). Query `pricedCurrency` !=
      // `orderCurrency` to find candidates for INF-566 rows 1 and 3.
      pricedCurrency,
      orderCurrency,
      currencyDiverged:
        pricedCurrency !== null &&
        orderCurrency !== null &&
        pricedCurrency !== orderCurrency,
      paymentMethodType: paymentMethod?.type,
      pricedCheckoutId: orderPriceSummary?.checkoutId,
    })

    // Diagnostic: detect "photo book size divergence" — the order's book size
    // (orderProducts[0].id, sourced from singleProductCheckout.itemId) differs from the size the
    // price was computed for (orderPriceSummary.products[].pricingProduct.tileSize). Seen as a
    // /v5/order/photo-books price_mismatch where the customer priced one book size (e.g.
    // BOOK_10X10) but the order POST carried a stale size (e.g. BOOK_8X8) — likely a stale
    // single-product snapshot restored from sessionStorage or the post-payment redirect backup.
    // Query Loggly for `order/pricing book size divergence` with bookSizeMismatch=true. COR-1113.
    if (orderParams?.productType === PRODUCT_TYPES.PHOTO_BOOK) {
      const orderBookSize = orderParams?.orderProducts?.[0]?.id ?? null
      const pricedBookSize =
        orderPriceSummary?.products?.[0]?.pricingProduct?.tileSize ?? null
      const bookSizeMismatch =
        orderBookSize !== null &&
        pricedBookSize !== null &&
        orderBookSize !== pricedBookSize
      logger.info('order/pricing book size divergence', {
        bookSizeMismatch,
        orderBookSize,
        pricedBookSize,
        paymentMethodType: paymentMethod?.type,
        pricedCheckoutId: orderPriceSummary?.checkoutId,
      })
    }

    logger.info('Sending order request', omit(orderParams, 'orderDraft'))

    const email = orderParams?.user?.email

    await applyPreOrderSideEffects({ email })

    return mixtilesAxios
      .post(this._getOrderEndpoint(orderPriceSummary), orderParams, {
        ...config,
        timeout: 60000,
      })
      .then(async (response) => {
        logger.info('finished creating order')
        const result = response.data
        const {
          order: resultOrder,
          isFirstOrder,
          emailAddedRecently,
          shouldApplyUpsellPromo,
        } = result

        const isGiftCard = isGiftCardOrder(orderPriceSummary?.productType)
        // Business vouchers ship; only a gift card has nothing to send.
        const hasShippingAddress =
          orderPriceSummary?.productType !== PRODUCT_TYPES.GIFT_CARD

        if (isGiftCard) {
          const giftCardType = orderItems[0]?.metadata?.giftCardType

          let orderTrackingData = {
            'USD Revenue': convertPriceToUSDCurrency(totalPrice, currency),
            'Order Revenue': totalPrice,
            Currency: currency,
            'Who Is It For': WHO_IS_IT_FOR_OPTIONS.GIFT,
            'Payment Intent ID': paymentIntentId,
            'Is Scheduled': orderItems[0]?.metadata?.isScheduled,
            'Order Type': orderPriceSummary?.productType,
            ...getGiftCardProps(orderPriceSummary, giftCardType),
          }

          if (orderParams.coupon) {
            orderTrackingData = {
              ...orderTrackingData,
              ...promoCodeManager.getPromoCodeAnalyticsData(),
            }
          }

          // Add gift card redeemed properties
          orderTrackingData = {
            ...orderTrackingData,
            ...getGiftCardRedeemedProps(orderPriceSummary),
            ...bagAnalytics,
            'Address Source': hasShippingAddress ? address?.source : undefined,
          }

          trackOrderCompleted({
            orderTrackingData,
            initialUserParams,
            prefix: 'Gift Card',
            isFirstOrder,
          })
        } else {
          const { shippingCost } = orderPriceSummary
          this._reportOrderCompletedAnalytics({
            resultOrder,
            totalPrice,
            tax,
            shippingCost,
            currency,
            tiles,
            orderData: orderParams,
            deliveryPromise,
            paymentMethod,
            address,
            initialUserParams,
            isFirstOrder,
            orderItems,
            orderPriceSummary,
            productType: orderPriceSummary.productType,
            emailAddedRecently,
            email,
            orderCompletedProps: {
              ...bagAnalytics,
              'Address Source': address.source,
            },
          })
        }

        const hasUsedCredits =
          orderParams.coupon ||
          orderParams.redemptions ||
          orderPriceSummary.freeTiles
        applyPostOrderSideEffects({
          deliveryPromise,
          hasUsedCredits,
          wasDiscountCouponRedeemed: wasCouponRedeemedInOrders([resultOrder]),
          isGiftCard,
        })

        return { order: resultOrder, shouldApplyUpsellPromo }
      })
      .catch((error) => {
        throw error
      })
  }

  static _prepareUser() {
    return {
      sessionToken: UserManager.getUserSessionToken(),
      email: UserManager.getUserEmail(),
    }
  }

  // Backwards compatibility - if it's an art order:
  // 1) localID must be unique, so we add the index to the localId so there won't be any situation where 2 of the same artworks were bought and they share the same local id. We still save the original tile.id at artworkId
  // 2) the tile will not have an 'originalUrl' property.
  // it is important that we only pass the thumbnailUrl to the backend
  // The backend knows how to extract the original image from that thumbnail url
  static _getArtUploadProperties({ localId, artworkId, thumbnailUrl }) {
    return {
      localId,
      artworkId,
      thumbnailUrl,
      disablePhotoEnhancement: true,
      context: TILE_CONTEXT.ART_FLOW,
    }
  }

  static _prepareUploads({ tiles }) {
    return tiles.map((tile, idx) => {
      const materialType = getTileMaterialType(tile)
      const materialParams = this._getMaterialParams({
        materialType,
        isCollage: isCollage(tile),
      })
      const { bleedType } = getBleed({ tileSize: tile.tileSize, materialType })

      const hasLowResWarn = shouldWarnTileLowQuality(tile)

      const uploadData = {
        localId: uuid(),
        url: tile.url ?? tile.originalUrl, // url should always be available unless we order regular centerpiece (not from photo walls)
        userDidCrop: tile.userDidCrop,
        cropRect:
          getTileCropParams(tile) || tile.cropParams[TILE_SIZES.SQUARE_8X8],
        collectionProductId: tile.collectionProductId,
        context: TILE_CONTEXT.CLASSIC_FLOW,
        uploadedAt: tile.uploadedAt,
        hasLowResWarn,
        customCenterpieceProps: tile.customCenterpieceProps,
      }

      // Add a flag to indicate if the tile has faces
      if (tile.faces && tile.faces.length > 0) {
        uploadData.hasFaces = true
      } else {
        uploadData.hasFaces = false // sending false to avoid undefined so we can differentiate between tiles from iOS (without this change - for now)
      }

      // Add DPI and face metrics
      uploadData.dpi = getTileDPI(tile)
      uploadData.width = tile.width
      uploadData.height = tile.height
      if (tile.faces?.length > 0) {
        uploadData.faces = tile.faces
        const smallest = getSmallestFaceMetrics(tile)
        uploadData.smallestFaceAreaPixels = smallest?.area
        uploadData.smallestFaceRelativeSize = smallest?.relativeArea
        uploadData.faceCount = tile.faces.length
      }

      const { reason: upscaleEligibilityReason } =
        computeUpscaleEligibility(tile)
      uploadData.isUpscaleEligible = false
      uploadData.upscaleEligibilityReason = upscaleEligibilityReason

      // Add Content Tiles data
      if (tile.contentSet) {
        uploadData.contentSet = tile.contentSet
      }

      if (isCenterpiece(tile)) {
        const centerpieceProperties = {
          isCenterpiece: true,
          tileContent: TILE_CONTENT.CENTERPIECE,
          isCustomized: tile.isCustomized,
          tileSize: TILE_SIZES.RECTANGLE_25X8,
          disablePhotoEnhancement: true, // According to request of industrial design team
          frameColor: FRAME_COLORS.NONE, // Otherwise will take default frame color from the order (and make problems with framed orders)
          tileType: TILE_TYPES.EDGE, // Otherwise will take default frame color from the order (and make problems with framed orders)
          tileFilter: {
            id: 'dynamic_backend',
            matting: {
              size: 0,
            },
            frame: {
              color: 'FRAMELESS',
            },
            imageEffect: {
              name: 'original',
            },
          },
        }

        Object.assign(uploadData, {
          ...centerpieceProperties,
          ...materialParams,
          bleedType,
          cropRect:
            materialType === MATERIAL_TYPES.CANVAS
              ? {}
              : FRAMELESS_CENTERPIECE_CROP_RECT,
        })
      }

      if (tile.cartItemId) {
        Object.assign(uploadData, { cartItemId: tile.cartItemId })
      }

      if (
        tile.itemType === ORDER_ITEM_TYPE.SINGLE_TILES &&
        !isCenterpiece(tile)
      ) {
        const { tileSize } = tile
        const cropRect = getTileCropParams(tile) || tile.cropParams[tileSize]
        const { frameColor, matting, mattingColor, filter, id } =
          getTileStyleData(tile)
        const canvasWrap = isCanvasGenerativeWrap({
          materialType,
          tileContent: tile.tileContent,
          experimentManager,
        })
          ? { type: 'generative' }
          : undefined
        uploadData.tileContent = tile.tileContent
        uploadData.context = TILE_CONTEXT.SINGLE_FLOW
        uploadData.photoStyleId = id
        if (isCollage(tile)) {
          uploadData.collage = this._getCollageParams(tile.collage)
          uploadData.disablePhotoEnhancement = true
        }
        this._setTileParamsToUpload(uploadData, {
          tileSize,
          frameColor,
          matting,
          mattingColor: getMatColorValue(mattingColor),
          filter,
          cropRect,
          canvasWrap,
          materialParams,
          bleedType,
        })
      }

      if (isArtPhoto(tile)) {
        // On the designer we don't have the actual art photo (but a thumbnail).
        // Therefore, we need to scale up the crop params so it'll be relative to the actual artwork
        const realArtworkDimensions = calculateArtworkDimensions(
          tile.artwork.width,
          tile.artwork.height
        )
        uploadData.cropRect = scaleCropParams(
          uploadData.cropRect,
          realArtworkDimensions.width / tile.width
        )
        Object.assign(
          uploadData,
          this._getArtUploadProperties({
            localId: `${uploadData.localId}_${idx}`,
            artworkId: tile.artwork.uid,
            thumbnailUrl: tile.url,
          })
        )
      }

      if (isAIGenPhoto(tile) && tile.aiTemplate) {
        uploadData.aiTemplate = tile.aiTemplate
      }

      // Use the explicit pricingProduct mechanism
      // Added to avoid pricing mismatch when using an old client that does not use the explicit pricingProduct
      // since the server build a pricingProduct based on the uploads, for old clients it should ignore the frameColor
      uploadData.explicitPricingProduct = true

      return uploadData
    })
  }

  static _getMaterialParams({ materialType, isCollage }) {
    const noStyling = {
      frameColor: FRAME_COLORS.NONE,
    }

    if (!isMattingEnabledForMaterial({ materialType })) {
      noStyling.matting = MATTING.NONE
      noStyling.mattingColor = DEFAULT_MAT_COLOR
    }

    switch (materialType) {
      case MATERIAL_TYPES.CANVAS:
        return {
          materialType,
          wrapsColor: isCollage ? CANVAS_WRAPS_COLOR.WHITE : null,
          ...noStyling,
        }
      case MATERIAL_TYPES.FRAMELESS:
        return {
          materialType: MATERIAL_TYPES.CLASSIC,
          ...noStyling,
        }
      case MATERIAL_TYPES.STICKER:
        return {
          materialType,
          ...noStyling,
        }
      case MATERIAL_TYPES.CLASSIC:
      default:
        return {
          materialType,
        }
    }
  }

  static _getCollageParams(collage) {
    const layout = pick(collage.layout, [
      'uid',
      'portfolioVariant',
      'photosCount',
      'style',
    ])
    const photos = mapValues(collage.photos, (photo) => ({
      url: photo.url,
      cropRect: getPhotoCroppingParams(photo)?.cropParams,
    }))
    const containsText = !!(
      collage?.collageText?.showText && collage?.collageText?.text
    )
    const text = containsText
      ? {
          text: collage.collageText.text,
          textStyleId: collage.collageText.textStyleId,
        }
      : null
    return {
      text,
      layout,
      photos,
    }
  }

  static _setTileParamsToUpload(
    uploadData,
    {
      tileSize,
      materialType,
      frameColor,
      matting,
      mattingColor,
      filter,
      cropRect,
      canvasWrap,
      materialParams = {},
      bleedType,
    }
  ) {
    const {
      frameColor: overrideFrameColor,
      matting: overrideMatting,
      mattingColor: overrideMattingColor,
    } = materialParams
    frameColor = overrideFrameColor || frameColor
    matting = overrideMatting || matting
    mattingColor = overrideMattingColor || mattingColor
    const tileType =
      frameColor === TILE_FRAMES.NONE ? TILE_TYPES.EDGE : TILE_TYPES.FRAMED
    const tileFilter = getTileFilter({
      tileSize,
      frameColor,
      matting,
      mattingColor,
    })
    const lutFilterName = filter && filter !== FILTERS.NONE ? filter : null

    Object.assign(uploadData, {
      tileSize,
      materialType,
      frameColor,
      tileType,
      tileFilter,
      cropRect,
      lutFilterName,
      ...materialParams,
      bleedType,
      ...(canvasWrap && { canvasWrap }),
    })
  }

  static preparePaymentData({
    totalPrice,
    paymentMethod,
    paymentMethodId,
    paymentIntentId,
  }) {
    if (totalPrice === 0) {
      // Lets users purchase without a payment method when using tiles credit
      return {}
    }
    const paymentData = {}
    paymentData.paymentMethod = paymentMethod.type

    switch (paymentMethod.type) {
      case PaymentMethodType.PAYPAL:
        paymentData.payPalTransactionId = paymentMethod.transactionId
        paymentData.paypalAccountName = PaymentManager.getPayPalAccountName()
        paymentData.paypalClientId = getPayPalKey(
          PaymentManager.getPayPalAccountName()
        )
        break
      case undefined:
      case null:
        throw new Error(
          `Received payment method without a type. paymentMethodId: ${paymentMethodId}`
        )
      default:
        paymentData.stripeCustomerId = PaymentManager.getStripeCustomer()?.id
        paymentData.stripeSourceId = paymentMethod.id
        paymentData.stripeAccountName = PaymentManager.getStripeAccountName()
        paymentData.stripeAccountPublicKey = PaymentManager.getStripeKey()
        paymentData.stripePaymentMethodId = paymentMethodId
        paymentData.stripePaymentIntentId = paymentIntentId
        break
    }
    return paymentData
  }

  static _prepareAddress({ address, shouldValidateAddress }) {
    getAddressStore().setAddressValidationSkipped(!shouldValidateAddress)
    return normalizeAddress(address)
  }

  static _prepareTileType({ uploads }) {
    const allUploadsFramed = uploads.every(
      (upload) => upload.tileType === TILE_TYPES.FRAMED
    )
    const allUploadsFrameless = uploads.every(
      (upload) => upload.tileType === TILE_TYPES.EDGE
    )

    if (allUploadsFramed) {
      return TILE_TYPES.FRAMED
    }

    if (allUploadsFrameless) {
      return TILE_TYPES.EDGE
    }

    // tileType should be omitted in the case of mixed framed & frameless order
    return undefined
  }

  static _getOrderPhotos({ orderData }) {
    try {
      if (orderData?.productType === PRODUCT_TYPES.PHOTO_BOOK) {
        return orderData.orderProducts.flatMap((orderProduct) =>
          getSerializedBookPhotos(
            orderProduct.metadata.cover,
            orderProduct.metadata.pages
          )
        )
      } else {
        return orderData.uploads
          .filter((upload) => upload.tileContent !== TILE_CONTENT.CENTERPIECE)
          .map((upload) =>
            upload.tileContent === TILE_CONTENT.COLLAGE
              ? Object.values(upload.collage.photos)
              : upload
          )
          .flat()
      }
    } catch (e) {
      logger.error('Failed calculating order photos', e)
      return null
    }
  }

  static buildOrderCompletedTrackingData({
    resultOrder,
    totalPrice,
    tax,
    shippingCost,
    currency,
    tiles,
    orderData,
    deliveryPromise,
    paymentMethod,
    address,
    orderItems,
    orderPriceSummary,
    productType,
    emailAddedRecently,
    email,
    orderCompletedProps = {}, // These props will be sent only once in Order Completed, and not sub-events like PRODUCT_ORDERED)
    // Cross-cutting id added to every event (Order Completed + sub-events).
    orderGroupUid,
  }) {
    try {
      // Make sure reporting exceptions won't affect the order resetting afterwards
      const revenue = calculateRevenue(totalPrice, tax)
      const tileSizes = getTilesSizesNames(tiles)
      const creditUsed = orderData.credit || 0
      const orderPhotos = this._getOrderPhotos({ orderData })
      let contentParams
      let orderTrackingData = {
        'Order UID': resultOrder ? resultOrder.uid : '',
        'USD Revenue': convertPriceToUSDCurrency(revenue, currency),
        'Order Revenue': roundDecimal(revenue),
        'Credit Redeemed': creditUsed,
        'Credit Redeemed USD': convertPriceToUSDCurrency(creditUsed, currency),
        Currency: currency,
        'Order Tiles': tiles.length,
        'Destination Country': COUNTRIES[address.countryId].name,
        'Local ID': orderData.localId,
        'Gift Note Added': !!giftNoteManager.getGiftNote(),
        'Shipping Option':
          deliveryPromise && deliveryPromise.shippingOptionName,
        'Express Shipping Selected': isOptionExpress(deliveryPromise),
        'Express Shipping Offered': !!(
          shippingOptionsManager.getStoredShippingOptions().shippingOptions ||
          []
        ).find(isOptionExpress),
        'Priority Shipping Selected': isOptionPriority(deliveryPromise),
        'Priority Shipping Offered': !!(
          shippingOptionsManager.getStoredShippingOptions().shippingOptions ||
          []
        ).find(isOptionPriority),
        'User Email': email,
        'Hash Email': email && getSha256(email),
        'Who Is It For': StorageManager.get(WHO_IS_IT_FOR_KEY) || null,
        'USD Shipping Cost': convertPriceToUSDCurrency(shippingCost, currency),
        'Tile Sizes': [...tileSizes],
        'Tile Materials': getTilesMaterials(tiles),
        'Photo Count': orderPhotos?.length || null,
        'Credits Used': orderPriceSummary.freeTilesData.freeTilesCreditUsed,
        'Checkout Id': orderPriceSummary.checkoutId,
        Source: tiles.map((tile) => tile.source),
        ...getCartUpsellTilesAnalyticsProps(tiles),
        ...getGiftCardProps(orderPriceSummary),
        ...getCropsDistribution(orderPhotos),
        ...getSmartCropAdjustmentMetrics(orderPhotos),
        ...getOrderFaceAndDPIMetrics(tiles),
      }

      const contentTilesCountInOrder = orderData.contentTilesCount
      if (contentTilesCountInOrder > 0) {
        orderTrackingData['Content Tiles Set'] = orderData.contentSets
        orderTrackingData['Content Tiles Count'] = contentTilesCountInOrder
      }

      const artTilesCountInOrder = orderData.artTilesCount
      if (artTilesCountInOrder > 0) {
        orderTrackingData['Art Tiles'] = orderData.artTileUrls
        orderTrackingData['Art Tiles Count'] = artTilesCountInOrder
      }

      // Tiles generated through the "more styles" pet variations sheet funnel.
      const petVariationTilesCount = tiles.filter(
        (tile) => isAIGenPhoto(tile) && tile.aiTemplate?.isVariation
      ).length
      if (petVariationTilesCount > 0) {
        orderTrackingData['Pet Variation Tiles Count'] = petVariationTilesCount
      }

      if (orderData.topQueue) {
        orderTrackingData['Delivery Preceded'] = true
      }

      if (totalPrice > 0) {
        orderTrackingData['Payment Method'] = paymentMethod.type
      }
      const promoCodeAnalytics = orderData.coupon
        ? promoCodeManager.getPromoCodeAnalyticsData()
        : {}
      orderTrackingData = {
        ...orderTrackingData,
        ...promoCodeAnalytics,
        ...getPricingAnalyticsData(orderPriceSummary),
      }

      if (StorageManager.get(ATTRIBUTION_SURVEY_KEY)) {
        orderTrackingData['Attribution Survey'] = StorageManager.get(
          ATTRIBUTION_SURVEY_KEY
        )
      }

      let totalUpscaleEligibleCount = 0
      let totalOrderTilesCount = 0
      const centerpieces = tiles.filter((tile) => isCenterpiece(tile))
      if (centerpieces.length > 0) {
        orderTrackingData['Centerpiece Name'] = centerpieces.map(
          (c) => c.centerpieceTitle
        )
      }

      if (orderItems) {
        const tileDistribution =
          orderItems[0]?.orderTiles?.length > 0
            ? getTileDistribution(orderItems[0]?.orderTiles)
            : {}
        orderTrackingData = {
          ...orderTrackingData,
          ...generateCartAnalytics(orderItems, orderPriceSummary),
          ...tileDistribution,
        }

        orderItems.forEach((item) => {
          const { orderTiles = [], itemType, totalPrice, materialType } = item

          let artPhotoTotalRevenue = 0
          let aiTemplatesTotalRevenue = 0
          let only8x8TotalRevenue = 0 // Both 8x8 and signs according to initial data requirement (before we had sizes), so this assumption might be deprecated
          const aiTemplatesByCategoryRevenue = {}
          const aiTemplatesByCategoryCount = {}
          let singleTileOrderedData = {}
          let upscaleEligibleCount = 0
          if (productType === PRODUCT_TYPES.CLASSIC) {
            totalOrderTilesCount += orderTiles.length
            const tileSizesCount = getTileSizesCount(orderTiles)
            // Mark non 8x8 for product and order. According to data team, centerpiece also counts as
            // 8x8 because clients were already exposed to those walls in the old version
            const isNon8x8Item =
              (tileSizesCount[TILE_SIZES.SQUARE_8X8] || 0) +
                (tileSizesCount[TILE_SIZES.RECTANGLE_25X8] || 0) <
              orderTiles.length
            if (isNon8x8Item) {
              orderTrackingData['Non 8x8 Order'] = 'True'
              if (itemType === ORDER_ITEM_TYPE.SINGLE_TILES) {
                orderTrackingData['Non 8x8 Single Tiles'] = 'True'
              } else if (itemType === ORDER_ITEM_TYPE.PHOTO_WALL) {
                orderTrackingData['Non 8x8 Photo Wall'] = 'True'
              }
            }
            const lowQualityTiles = tiles.filter((tile) =>
              shouldWarnTileLowQuality(tile)
            )

            singleTileOrderedData = {
              'Non 8x8 Item': isNon8x8Item,
              'Tile Sizes Count': Object.keys(tileSizesCount).length,
              'Email Added Recently': emailAddedRecently,
              'Low-Resolution Tiles Count': lowQualityTiles.length,
              ...getTileDistribution(orderTiles),
              ...generateProductCountEventProperties(),
              ...getMaterialProperties(materialType, tiles),
            }

            const isPhotoWall = item.itemType === ORDER_ITEM_TYPE.PHOTO_WALL
            const params = isPhotoWall
              ? {
                  productId: item.itemId,
                  productName: item.title,
                  wallName: item.title,
                  productType: ORDER_ITEM_TYPE.PHOTO_WALL,
                }
              : {}
            analytics.track(EVENT_NAMES.TILES_ORDERED, {
              ...(orderGroupUid && { 'Order Group UID': orderGroupUid }),
              ...params,
              'Product Type': isPhotoWall
                ? ORDER_ITEM_TYPE.PHOTO_WALL
                : productType,
              ...getGiftCardRedeemedProps(orderPriceSummary),
              Tiles: orderTiles.map((tile) => {
                const tileSize = getTileSize(tile)
                const materialType = getTileMaterialType(tile)
                const { filter, matting, frameColor, color, mattingColor } =
                  getTileStyleData(tile)
                const listPrice = getTileOriginalPrice({
                  tileSize,
                  materialType,
                  orderPriceSummary,
                  frameColor,
                })
                const tileRevenueUSD = convertPriceToUSDCurrency(
                  calculateTileRevenue(
                    centerpieces,
                    orderPriceSummary,
                    listPrice.value,
                    tile,
                    totalPrice,
                    orderTrackingData['USD Revenue']
                  ),
                  listPrice.currency
                )
                if (
                  tileSize === TILE_SIZES.SQUARE_8X8 ||
                  tileSize === TILE_SIZES.RECTANGLE_25X8
                ) {
                  only8x8TotalRevenue += tileRevenueUSD
                }
                artPhotoTotalRevenue += isArtPhoto(tile) ? tileRevenueUSD : 0
                if (isAIGenPhoto(tile)) {
                  aiTemplatesTotalRevenue += tileRevenueUSD
                  const category = tile.aiTemplate?.templateCategory
                  if (category) {
                    aiTemplatesByCategoryRevenue[category] =
                      (aiTemplatesByCategoryRevenue[category] || 0) +
                      tileRevenueUSD
                    aiTemplatesByCategoryCount[category] =
                      (aiTemplatesByCategoryCount[category] || 0) + 1
                  }
                }
                const tileProps = {
                  ...(orderGroupUid && { 'Order Group UID': orderGroupUid }),
                  'Tile UID': tile.uid,
                  'Tile ID': tile.id,
                  'Upload Src': tile.uploadSrc,
                  'Category Name': item.metadata?.categoryName,
                  'Creative Type': tile.tileContent,
                  ...(isCollage(tile)
                    ? getCollageProperties(tile)
                    : { 'Photos Count': 1 }),
                  Filter: filter,
                  'Frame Type': isFramedMaterial(materialType)
                    ? frameColor || color
                    : FRAME_COLORS.NONE,
                  Matting: matting,
                  'Mat Color': mattingColor,
                  'Frame Size': tileSize,
                  'Order UID': orderTrackingData['Order UID'],
                  'USD Revenue Per Tile': tileRevenueUSD,
                  'Product Type': tile.itemType,
                  'Product Name': item.title || 'Single Tiles',
                  'List Price In USD': convertPriceToUSDCurrency(
                    listPrice.value,
                    listPrice.currency
                  ),
                  'Is Low-Resolution': shouldWarnTileLowQuality(tile),
                  ...promoCodeAnalytics,
                  ...getPricingAnalyticsData(orderPriceSummary),
                  ...getMaterialProperties(materialType, tiles),
                  ...getPromiseDateProps(),
                  ...getMinimumOrderFeeProps(orderPriceSummary),
                  ...getShippingPricingProps(orderPriceSummary),
                  ...getTileArtworkProperties(tile),
                  ...getTileFaceMetrics(tile),
                  ...(() => {
                    const { eligible, reason, factors } =
                      computeUpscaleEligibility(tile)
                    if (eligible) upscaleEligibleCount++
                    return {
                      'Will Be Upscaled': eligible,
                      'Upscale Eligibility Reason': reason,
                      ...factors,
                    }
                  })(),
                }

                if (isArtPhoto(tile)) {
                  analytics.track(EVENT_NAMES.ARTWORK_ORDERED, tileProps)
                }

                if (isAIGenPhoto(tile)) {
                  analytics.track(EVENT_NAMES.AI_ARTWORK_ORDERED, {
                    ...tileProps,
                    ...getAIGenPhotoTileAnalyticsProps(tile),
                  })
                }

                return tileProps
              }),
            })

            if (orderTiles.some((t) => isPhoto(t) || isCollage(t))) {
              orderTrackingData['Photos Order'] = 'True'
            }

            if (orderTiles.some((t) => isArtPhoto(t))) {
              orderTrackingData['Art Collection Order'] = 'True'
              orderTrackingData['ArtPhoto USD Revenue'] = artPhotoTotalRevenue
            }

            const aiTemplateAnalytics = getAITemplateAnalytics(
              orderTiles,
              aiTemplatesByCategoryRevenue,
              aiTemplatesByCategoryCount
            )
            Object.assign(orderTrackingData, aiTemplateAnalytics)
          }

          const actualPrice = item.priceAfterDiscount

          orderTrackingData['Order Items Count'] =
            orderPriceSummary?.products?.length || orderItems.length

          const revenueShare8x8 = roundDecimal(
            (100 * only8x8TotalRevenue) / orderTrackingData['USD Revenue']
          )
          const productOrderedData = {
            ...(orderGroupUid && { 'Order Group UID': orderGroupUid }),
            ...generateCartItemAnalytics(
              item,
              experimentManager.isEnabled('pricing_photo_book_price_tier')
            ),
            'Total Price in USD - Actual': convertPriceToUSDCurrency(
              actualPrice,
              currency
            ),
            'Order UID': orderTrackingData['Order UID'],
            'USD Revenue': orderTrackingData['USD Revenue'],
            '8x8 Revenue Percentage': revenueShare8x8,
            'Non 8x8 Revenue Percentage': 100 - revenueShare8x8,
            'ArtPhoto USD Revenue Share': roundDecimal(
              (100 * artPhotoTotalRevenue) / orderTrackingData['USD Revenue']
            ),
            'AI Templates USD Revenue': aiTemplatesTotalRevenue,
            'AI Templates USD Revenue Share': roundDecimal(
              (100 * aiTemplatesTotalRevenue) / orderTrackingData['USD Revenue']
            ),
            'Order Items Count': orderTrackingData['Order Items Count'],
            ...promoCodeAnalytics,
            ...getPricingAnalyticsData(orderPriceSummary),
            ...getPromiseDateProps(),
            ...getMinimumOrderFeeProps(orderPriceSummary),
            ...getShippingPricingProps(orderPriceSummary),
            ...getGiftCardRedeemedProps(orderPriceSummary),
            ...singleTileOrderedData,
            ...getAITemplateCategoryRevenueShares(
              aiTemplatesByCategoryRevenue,
              orderTrackingData['USD Revenue']
            ),
          }

          productOrderedData['Will Be Upscaled Count'] = upscaleEligibleCount
          productOrderedData['Will Be Upscaled Percentage'] =
            orderTiles.length > 0
              ? roundDecimal((upscaleEligibleCount / orderTiles.length) * 100)
              : 0

          analytics.track(EVENT_NAMES.PRODUCT_ORDERED, productOrderedData)
          totalUpscaleEligibleCount += upscaleEligibleCount
        })

        if (productType === PRODUCT_TYPES.PHOTO_BOOK) {
          orderTrackingData['Book Order'] = 'True'
          Object.assign(orderTrackingData, getOrderPhotoBooksProps(orderItems))
        }

        if (productType === PRODUCT_TYPES.CLASSIC) {
          contentParams = getViewContentParams({ tiles })
        }
      }

      // Add gift card redeemed properties to order tracking data
      orderTrackingData = {
        ...orderTrackingData,
        ...getGiftCardRedeemedProps(orderPriceSummary),
        'Will Be Upscaled Count': totalUpscaleEligibleCount,
        'Will Be Upscaled Percentage':
          totalOrderTilesCount > 0
            ? roundDecimal(
                (totalUpscaleEligibleCount / totalOrderTilesCount) * 100
              )
            : 0,
        ...orderCompletedProps,
      }

      return { orderTrackingData, contentParams }
    } catch (error) {
      analytics.track(EVENT_NAMES.ORDER_COMPLETE_REPORT_FAILED, {
        Error: extractErrorMessage(error),
        orderData,
      })
      logger.error('Error reporting order completed analytics', error)
      return undefined
    }
  }

  static _reportOrderCompletedAnalytics(args) {
    const result = this.buildOrderCompletedTrackingData(args)
    if (!result) return
    try {
      trackOrderCompleted({
        orderTrackingData: result.orderTrackingData,
        initialUserParams: args.initialUserParams,
        isFirstOrder: args.isFirstOrder,
        orderItems: args.orderItems,
        emailAddedRecently: args.emailAddedRecently,
        orderPriceSummary: args.orderPriceSummary,
        contentParams: result.contentParams,
      })
    } catch (error) {
      analytics.track(EVENT_NAMES.ORDER_COMPLETE_REPORT_FAILED, {
        Error: extractErrorMessage(error),
        orderData: args.orderData,
      })
      logger.error('Error reporting order completed analytics', error)
    }
  }

  static _getProductsTotalRevenue(products, requiredProductType) {
    return products
      .filter((item) => item.set?.setData?.productType === requiredProductType)
      .map((item) => item.set?.setData?.price)
      .reduce(
        (currentSum, price) => (price ? currentSum + price : currentSum),
        0
      )
  }
}

/**
 * Calculate net revenue for analytics - price without taxes (for all the products and shipping in the order)
 * Logic:
 * 1. If the country is in a "VAT" regime, it means the displayed price ALREADY includes the tax.
 * Therefore, we strip that portion by multiplying by 100 / (100 + VAT%). To receive the price without this tax.
 * 2. If the country uses "Sales Tax", that is, the tax is added on top of the price, we simply subtract that amount.
 */
function calculateRevenue(price, tax = 0) {
  const vatPercentage = getVATPercentage()

  if (vatPercentage > 0 && tax > 0) {
    logger.error(
      "Customers has both VAT and Tax, which shouldn't happen",
      null,
      { vatPercentage, tax }
    )
  }
  const netPrice = price - tax
  // Handle case of negative revenue - for example it happens if a customer ordered with gift card / credits
  if (netPrice < 0) {
    return 0
  }
  return netPrice * (100 / (100 + vatPercentage))
}

function calculateTileRevenue(
  centerpieces,
  orderPriceSummary,
  listPrice,
  tile,
  totalPrice,
  revenue
) {
  if (orderPriceSummary.validated) {
    const tilePrice = getTileActualPrice(tile, orderPriceSummary) // may include VAT
    const { productTotalPrice, shippingCost } = orderPriceSummary

    if (!shippingCost || !productTotalPrice) {
      return stripVatIfNeeded(tilePrice)
    } else {
      // Add relative shipping cost to the tile USD price
      const shippingShare = (tilePrice / productTotalPrice) * shippingCost
      return roundDecimal(stripVatIfNeeded(tilePrice + shippingShare))
    }
  }
  const containCenterpieceFullPrice =
    centerpieces.length > 0 && !orderPriceSummary.centerpiecesDiscount
  let tileRevenue
  if (containCenterpieceFullPrice) {
    if (isCenterpiece(tile)) {
      tileRevenue =
        orderPriceSummary.centerpiecesTotalPrice / centerpieces.length
    } else {
      tileRevenue =
        listPrice *
        ((revenue - orderPriceSummary.centerpiecesTotalPrice) /
          (totalPrice.value - orderPriceSummary.centerpiecesTotalPrice))
    }
  } else {
    tileRevenue = listPrice * (revenue / totalPrice.value)
  }
  return tileRevenue
}

function getDiscountCoupon(orderPriceSummary) {
  if (orderPriceSummary.validated) {
    // Taking the promocode from the orderPriceSummary to avoid sync issue where a promocode is added in
    // another tab and the orderPriceSummary hasn't been refreshed. The old flow has this issue as well but because
    // we introduced skipMismatch we never caught onto it :')
    return orderPriceSummary.promoCode
  } else {
    return orderPriceSummary.discountCouponDidApply
      ? promoCodeManager.getDiscountCouponCode()
      : undefined
  }
}

function getTileOriginalPrice({
  tileSize,
  frameColor,
  materialType,
  orderPriceSummary,
}) {
  const tilePricingProduct = { tileSize, materialType, frameColor }
  const lineItem = orderPriceSummary.summaryItems.find(
    isTileEqualToLineItem(tilePricingProduct)
  )
  if (!lineItem) {
    logger.error("Couldn't find lineItem for tile original price", null, {
      tilePricingProduct,
      summaryPricingProducts: orderPriceSummary.summaryItems
        .filter((item) => item.pricingProduct)
        .map((item) => item.pricingProduct),
    })
  }

  return {
    value: lineItem.priceBeforeDiscount,
    currency: orderPriceSummary.currency,
  }
}

function getTileActualPrice(tile, orderPricingSummary) {
  const tileSize = getTileSize(tile)
  const materialType = getTileMaterialType(tile)
  const frameColor = getTileStyleData(tile).frameColor
  const tilePricingProduct = { tileSize, materialType, frameColor }
  const lineItem = orderPricingSummary.summaryItems.find(
    isTileEqualToLineItem(tilePricingProduct)
  )
  if (!lineItem) {
    logger.error("Couldn't find lineItem for tile", {
      tile,
      orderPricingSummary,
    })
    return 0
  }
  return lineItem.priceAfterDiscount
}

/**
 * Generates AI Templates analytics data from order tiles
 * @param {Array} orderTiles - Array of tiles in the order
 * @param {Object} revenueByCategory - Object mapping category to total revenue
 * @param {Object} countByCategory - Object mapping category to tile count
 * @returns {Object} Analytics data for AI Templates including category breakdowns
 */
function getAITemplateAnalytics(
  orderTiles,
  revenueByCategory,
  countByCategory
) {
  const aiTiles = orderTiles.filter((t) => isAIGenPhoto(t))

  if (aiTiles.length === 0) {
    return {}
  }

  const analytics = {
    'AI Templates Order': 'True',
    'AI Templates USD Revenue': Object.values(revenueByCategory).reduce(
      (sum, val) => sum + val,
      0
    ),
    'AI Templates Ordered': [
      ...new Set(aiTiles.map((tile) => tile.aiTemplate?.templateName)),
    ].join(','),
  }

  // Add category-based analytics
  const categories = Object.keys(revenueByCategory)
  if (categories.length > 0) {
    analytics['AI Templates Categories Ordered'] = categories.join(',')

    categories.forEach((category) => {
      const categoryCapitalized = capitalizeFirstLetter(category)
      analytics[`AI Templates ${categoryCapitalized} Order`] = 'True'
      analytics[`AI Templates ${categoryCapitalized} USD Revenue`] =
        revenueByCategory[category]
      analytics[`AI Templates ${categoryCapitalized} Count`] =
        countByCategory[category]
    })
  }

  return analytics
}

/**
 * Calculates revenue share percentages for AI Template categories
 * @param {Object} revenueByCategory - Object mapping category to total revenue
 * @param {number} totalOrderRevenue - Total order revenue in USD
 * @returns {Object} Revenue share percentages by category
 */
function getAITemplateCategoryRevenueShares(
  revenueByCategory,
  totalOrderRevenue
) {
  const shares = {}

  Object.keys(revenueByCategory).forEach((category) => {
    const categoryCapitalized = capitalizeFirstLetter(category)
    shares[`AI Templates ${categoryCapitalized} USD Revenue Share`] =
      roundDecimal((100 * revenueByCategory[category]) / totalOrderRevenue)
  })

  return shares
}

export default OrderManager

function stripVatIfNeeded(amount) {
  const vatPercentage = getVATPercentage()
  return vatPercentage > 0 ? amount * (100 / (100 + vatPercentage)) : amount
}
